import './CourseDetail.scss'

import { useEffect, useState, useCallback, useContext } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Button, Col, Container, Form, Row, Tab, Tabs } from 'react-bootstrap'
import MediaQuery, { useMediaQuery } from 'react-responsive'
import Drawer from 'react-bottom-drawer'
import parse from 'html-react-parser'
import cn from 'classnames'

import {
  getFullAuthorById,
  getFullCourseById,
  isFreeCourse,
  LMS_TYPE_COURSES,
  LMS_TYPE_PREFIX,
  tryGetCourseField
} from 'components/common/api/Courses'
import {
  ROUTE_COURSES_ALL,
  getTopicPath,
  getCoursePath,
  ROUTE_MEMBERSHIP
} from 'components/common/helpers/Paths'
import CourseOutline from '../CourseOutline'
import {
  EVENT_CATEGORY_COURSE,
  EVENT_ACTION_PAGEVIEW,
  saveUserEvent,
  trackMetaEvent,
  trackGAEvent,
  saveUserEventDebounced
} from 'components/common/api/UserEvents'
import {
  ACTION_COMPLETE,
  tryUpdateCourseProgressForCourseObject
} from '../../common/api/CourseProgress'

import CourseDrawer from '../CourseDrawer'
import LessonVideo from 'components/lessons/LessonVideo'
import SessionsOutline from '../SessionsOutline'
import CourseContext from 'components/common/contexts/CourseContext'
import SubscriptionContext from 'components/common/contexts/SubscriptionContext'
import { buildRequestURL, TYPE_TOPIC } from 'components/common/api'
import { navigateTo } from 'components/common/navigation'
import ModalVideoPreview from '../../modal-video-preview'
import Loading from '../../../templates/Loading'
import Bookmark from '../../bookmark/Bookmark'
import WebShare from '../../web-share'

import videoPreviewImg from '../../../assets/images/video-preview-image.jpeg'

// Curried helper function, to make it quicker to try and assign ACF field values
const _tryGetSet = courseObj => (acfKey, setFunction) =>
  setFunction(tryGetCourseField(courseObj, acfKey))

const MembershipButton = (courseId, history) => (
  <Button
    variant='secondary'
    className='course-button-link mt-3 btn-lg'
    onClick={() => {
      // Track to Meta
      trackMetaEvent('AddToCart', {
        content_name: 'Clicked Subscribe'
      })

      // Track to GA
      trackGAEvent('add_to_cart', 'add_to_cart', 'Clicked Subscribe')

      // Navigate to subscription page
      const url = buildRequestURL(ROUTE_MEMBERSHIP, {
        returnPath: getCoursePath(courseId)
      })
      navigateTo(url, history)
    }}
  >
    Become a Member
  </Button>
)

/**
 * Course detail component. Used for displaying a Course along with its lessons.
 */
export default function CourseDetail({ cache, setReturnPath, setHeaderTitle }) {
  const history = useHistory()

  const { courseId } = useParams()
  const { progress } = useContext(CourseContext)
  const { userSubscription: subscription, userHasPremiumAccess } =
    useContext(SubscriptionContext)

  const [courseIsLocked, setCourseIsLocked] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingAuthors, setIsLoadingAuthors] = useState(true)
  const [nextStep, setNextStep] = useState()
  const [course, setCourse] = useState()
  const [courseAdaptedFrom, setCourseAdaptedFrom] = useState()
  const [coursePreview, setCoursePreview] = useState()
  const [coursePreviewTitle, setCoursePreviewTitle] = useState()
  const [coursePreviewSummary, setCoursePreviewSummary] = useState()
  const [coursePreviewStreamId, setCoursePreviewStreamId] = useState()
  const [coursePreviewCaptions, setCoursePreviewCaptions] = useState()
  const [courseDetailProgress, setCourseDetailProgress] = useState()
  const [courseLength, setCourseLength] = useState('')
  const [isVisible, setIsVisible] = useState(false)
  const [authors, setAuthors] = useState([])
  const [showVideo, setShowVideo] = useState(false)
  const [topicVideo, setTopicVideo] = useState({})
  const [autoplay, setAutoplay] = useState(false)
  const [sessions, setSessions] = useState([])
  const [activeTopic, setActiveTopic] = useState({})
  const [triggerRender, setTriggerRender] = useState({})

  const [courseDuration, setCourseDuration] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  })

  const { hours, minutes } = courseDuration

  const isLargeScreen = useMediaQuery({ minWidth: 768 })
  const iconColor = !isLargeScreen ? 'text-black' : 'text-white'

  const hasPreview = coursePreview || coursePreviewStreamId

  /**
   * Event handlers for closing the intro video preview modal and drawer functions
   */
  const handleClose = () => setShowVideo(false)
  const handleShow = () => setShowVideo(true)

  const openDrawer = () => setIsVisible(true)
  const closeDrawer = () => setIsVisible(false)

  /**
   * Tries to load authors for the course and fails silently if it fails.
   */
  const fetchAuthorsAsync = useCallback(async () => {
    // Return early if we don't have a course or already have the authors
    if (!course || authors.length) return

    try {
      const updated =
        course.authors !== '' &&
        (await Promise.all(
          course.authors.map(async author => await getFullAuthorById(author.ID))
        ))

      // Set Course authors
      setAuthors(updated)
      setIsLoadingAuthors(false)
    } catch (e) {
      console.error(e)
    } // Swallow exceptions
  }, [course, authors])

  /**
   * Event handler for starting the next step of the course
   * @returns
   */
  const handleButtonClick = () => {
    let url

    // First time?
    if (!nextStep) {
      const firstTopic = course.topics[0]
      url = getTopicPath(courseId, firstTopic.lesson, firstTopic.id)
      return navigateTo(url, history)
    }

    // Find user's next step
    const foundStep = courseDetailProgress.find(
      match => match.step === nextStep
    )

    // TODO: Handle for when the step might be a lesson ID
    if (!foundStep.post_type === `sfwd-${TYPE_TOPIC}`) return

    // Find the matching topic
    const foundTopic = course.topics.find(topic => topic.id === foundStep.step)

    // Abort if we didn't find a result
    if (!foundTopic) return

    url = getTopicPath(courseId, foundTopic.lesson, foundTopic.id)

    navigateTo(url, history)
  }

  /**
   * Helper function to get the duration of the course
   * @returns {string} Returns a string with the duration of the course
   */
  const getDurationOfCourse = () => {
    let duration = ''
    if (hours) duration += `${hours} hr `
    if (minutes) duration += `${minutes} min `
    // Don't show seconds on the course detail page
    // if (seconds) duration += `${seconds} sec`
    return duration
  }

  // Subscription hook
  useEffect(() => {
    // Return early if we don't have course or subscription data
    if (!course || !subscription) return

    const isFree = isFreeCourse(course)
    const isLocked = !userHasPremiumAccess && !isFree

    setCourseIsLocked(isLocked)
  }, [course, subscription, userHasPremiumAccess])

  // Main effect hook
  useEffect(() => {
    const cacheKey = `course_${courseId}`
    // Scroll back to top when returning
    window.scrollTo(0, 0)

    // Update header and return path
    setReturnPath(ROUTE_COURSES_ALL)
    setHeaderTitle(null)

    // Query full course details
    async function getCourseAsync() {
      // Return if we've already populated the course object
      if (!courseId || course) return

      // Check cache first
      const courseResult = await cache.tryGetAsyncResult(
        cacheKey,
        getFullCourseById,
        this,
        courseId
      )

      // Set Course details
      setCourse(courseResult)

      // Log course access
      saveUserEvent(EVENT_CATEGORY_COURSE, EVENT_ACTION_PAGEVIEW, courseId)
      setIsLoading(false)
    }
    getCourseAsync()
  }, [courseId, course, cache, setReturnPath, setHeaderTitle])

  /**
   * Event handler for lesson completion, marks the currently displayed content as "completed"
   */
  const handleComplete = async () => {
    // Return early if we don't have a course or active topic
    if (!course || !activeTopic) return

    // Use our course progress helper to mark this lesson or topic as complete
    const success = await tryUpdateCourseProgressForCourseObject(
      courseId,
      activeTopic,
      ACTION_COMPLETE
    )

    setTriggerRender(success)

    // Drop the LearnDash `sfwd-` prefix and the trailing "s" if there is one
    const category = activeTopic.type
      .replace(LMS_TYPE_PREFIX, '')
      .replace(/s+$/, '')

    // Log completion
    await saveUserEventDebounced(category, ACTION_COMPLETE, activeTopic.id)
  }

  // Course update "cascade"
  useEffect(() => {
    // Return early if we don't have a course or progress yet
    if (!progress || !course) return

    // Check to see if the user has started the course already
    const match = progress.find(match => match.course === parseInt(course.id))
    if (match) setNextStep(match.last_step)

    // Reference to helper function
    const tryGetSet = _tryGetSet(course)

    // Curried try get and set
    tryGetSet('course_adapted_from', setCourseAdaptedFrom)
    tryGetSet('course_length', setCourseLength)
    tryGetSet('course_preview', setCoursePreview)
    tryGetSet('course_preview_title', setCoursePreviewTitle)
    tryGetSet('course_preview_summary', setCoursePreviewSummary)
    tryGetSet('course_preview_video_captions', setCoursePreviewCaptions)
    tryGetSet('course_preview_mux_stream_id', setCoursePreviewStreamId)

    // Course duration
    const duration = tryGetCourseField(course, 'course_duration')
    if (duration) {
      const [hours, minutes, seconds] = duration.split(':')

      setCourseDuration({
        hours: parseInt(hours, 10),
        minutes: parseInt(minutes, 10),
        seconds: parseInt(seconds, 10)
      })
    }

    // Fetch authors
    fetchAuthorsAsync()
  }, [progress, course, fetchAuthorsAsync])

  useEffect(() => {
    window.scrollTo(0, 0)
    setActiveTopic(topicVideo)
  }, [topicVideo])

  return (
    <Loading isLoaded={isLoading || !course}>
      <Container
        fluid
        className={cn('course-detail-container', {
          'has-preview': coursePreview,
          'large-screen px-0': isLargeScreen
        })}
      >
        <div className='course-intro-video'>
          {isLargeScreen && (
            <img
              className='course-intro-image'
              src={
                course?.featured_image_url === ''
                  ? videoPreviewImg
                  : course?.featured_image_url
              }
              alt=''
              fetchpriority='high'
            />
          )}
          <div className='course-details'>
            <Row className='details'>
              {/* Course duration, title and description */}
              <Col xs={8}>
                {course?.clean_title && (
                  <h1 className='course-title mb-0'>
                    {parse(course.clean_title)}
                  </h1>
                )}
              </Col>
              <Col xs={4}>
                <div className='d-flex flex-column align-items-center'>
                  <span className='course-length-banner mb-2'>
                    {`${getDurationOfCourse()}`}
                    {courseLength > 1 ? ' Course' : ' Session'}
                  </span>

                  <div className='d-flex flex-row gap-3'>
                    <Bookmark
                      categoryObjectId={course?.id}
                      category={LMS_TYPE_COURSES}
                      color={iconColor}
                      size='fs-5'
                    />

                    <WebShare
                      text={course?.clean_title && parse(course.clean_title)}
                      color={iconColor}
                      size='fs-5'
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col sm={12}>
                {/* Course description */}
                {course?.description && (
                  <div className='course-description mt-3'>
                    {parse(course?.description)}
                  </div>
                )}
                {/* Course adapted from? */}
                {courseAdaptedFrom && (
                  <div className='adapted-from'>
                    Adapted from <i>{courseAdaptedFrom}</i>
                  </div>
                )}

                {/* Authors */}
                {authors.length > 0 && (
                  <div
                    className='authors desktop-hidden'
                    onClick={_ => openDrawer()}
                  >
                    <p className='muted'>
                      {authors.map(author => author.name).join(', ')}
                    </p>
                  </div>
                )}
              </Col>

              <MediaQuery minWidth={768}>
                <Col sm={12} className='pt-5'>
                  {/* Course video preview link */}
                  {coursePreview && (
                    <div
                      className='d-flex justify-content-start align-items-center course-preview-link'
                      onClick={handleShow}
                    >
                      <i className='bi bi-play-fill'></i>
                      <span>Play Introduction Video</span>
                    </div>
                  )}
                </Col>
              </MediaQuery>
            </Row>
          </div>
        </div>
        <div className='position-relative pt-5'>
          <Tab.Container
            defaultActiveKey='default'
            activeKey={autoplay ? 'autoplay' : 'default'}
          >
            <Row className='course-intro p-0 m-0'>
              <Col xs={12} className='p-0 intro-content'>
                {/* Does the user have access to the course? */}
                {courseIsLocked ? (
                  <div className='course-button-col'>
                    {MembershipButton(courseId, history)}
                  </div>
                ) : (
                  // Don't show autoplay if we don't have a course preview video
                  hasPreview && (
                    <Row>
                      <Col xs={4}>
                        <Form.Check
                          type='switch'
                          id='autoplay-switch'
                          label='Autoplay'
                          checked={autoplay}
                          onChange={_ => setAutoplay(!autoplay)}
                        />
                      </Col>
                      <Col xs={8} className='text-right'>
                        <small className='muted'>
                          Autoplay does not include tasks
                        </small>
                      </Col>
                    </Row>
                  )
                )}

                <Tab.Content>
                  <Tab.Pane eventKey='default' title='Sessions + tasks'>
                    <MediaQuery maxWidth={768}>
                      <Col xs={12} className='p-0'>
                        {/* Course video preview */}
                        {hasPreview && (
                          <div className='course-preview my-2'>
                            <LessonVideo
                              video={{
                                id: 0,
                                acf_extended: {
                                  video_url: coursePreview,
                                  mux_stream_id: coursePreviewStreamId,
                                  captions_url: coursePreviewCaptions
                                },
                                course_name: parse(course.clean_title),
                                title: `${parse(
                                  course.clean_title
                                )} Introduction Video`,
                                preview: course.featured_image_url
                              }}
                              key={courseId}
                              showWebShare={false}
                            />
                          </div>
                        )}
                      </Col>
                    </MediaQuery>
                    <Tabs
                      className='course-tabs ps-0 my-2'
                      defaultActiveKey={
                        coursePreviewTitle && coursePreviewSummary
                          ? 'intro'
                          : 'sessions'
                      }
                    >
                      {/* Don't show first tab if we don't have a preview summary */}
                      {coursePreviewTitle && coursePreviewSummary && (
                        <Tab eventKey='intro' title='Introduction'>
                          <h3 className='tab-title mt-4'>
                            {coursePreviewTitle && parse(coursePreviewTitle)}
                          </h3>
                          <div>
                            {coursePreviewSummary &&
                              parse(coursePreviewSummary)}
                          </div>
                        </Tab>
                      )}
                      <Tab eventKey='sessions' title='Sessions'>
                        {/* Does the user have access to the course? */}
                        {!courseIsLocked && (
                          <Button
                            variant='primary'
                            className='course-button-link my-3'
                            onClick={handleButtonClick}
                          >
                            {/* Has the user already started it? */}
                            {!!courseDetailProgress?.length
                              ? `Continue ${
                                  courseLength > 1 ? 'Course' : 'Session'
                                }`
                              : `Start ${
                                  courseLength > 1 ? 'Course' : 'Session'
                                }`}
                          </Button>
                        )}
                        <CourseOutline
                          course={course}
                          triggerRender={triggerRender}
                          setCourseDetailProgress={setCourseDetailProgress}
                        />
                      </Tab>
                    </Tabs>
                  </Tab.Pane>

                  <Tab.Pane eventKey='autoplay' title='Sessions only'>
                    <Col xs={12} className='p-0'>
                      <div className='sessions-video my-2'>
                        <LessonVideo
                          video={{
                            ...activeTopic,
                            course_name: parse(course?.clean_title || ''),
                            title: parse(activeTopic?.clean_title || '')
                          }}
                          key={activeTopic.id}
                          showWebShare={false}
                          autoplay={autoplay}
                          onVideoEnded={_ => {
                            handleComplete()

                            const activeIndex =
                              sessions &&
                              sessions.findIndex(
                                topic => topic.id === activeTopic.id
                              )

                            // If we're not on the last topic, go to the next one
                            if (activeIndex < sessions.length - 1)
                              setActiveTopic(sessions[activeIndex + 1])
                            else setAutoplay(false)
                          }}
                        />
                      </div>
                    </Col>
                    <h5 className='mt-3 fs-5 fw-normal'>Sessions</h5>
                    <hr />
                    <SessionsOutline
                      course={course}
                      setSessions={setSessions}
                      autoplayActiveIndex={activeTopic.id}
                      setTopicVideo={setTopicVideo}
                      setAutoplay={setAutoplay}
                    />
                  </Tab.Pane>
                </Tab.Content>

                {/* Does the user have access to the course? */}
                {courseIsLocked && (
                  <div className='course-button-col'>
                    {MembershipButton(courseId, history)}
                  </div>
                )}
              </Col>
            </Row>
          </Tab.Container>
        </div>
        {/*/!* Course authors *!/*/}
        <MediaQuery minWidth={768}>
          {!isLoadingAuthors && authors.length > 0 && (
            <div className='authors-wrapper'>
              <h3>About the author{authors.length > 1 && 's'}</h3>
              <Row>
                {authors.map(author => (
                  <Col key={author.id}>
                    <div className='author-wrapper'>
                      <div className='author-image'>
                        <img
                          src={author.authorImage}
                          alt={author.name}
                          loading='lazy'
                        />
                      </div>
                      <div className='author-name'>
                        <h5>{author.name}</h5>
                      </div>
                      <div className='author-bio'>{parse(author.bio)}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </MediaQuery>
      </Container>

      {/* Author info, mobile only */}
      {course && authors.length > 0 && (
        <Drawer
          duration={250}
          hideScrollbars={true}
          onClose={closeDrawer}
          isVisible={isVisible}
          className='course-drawer'
        >
          <CourseDrawer authors={authors} closeDrawer={closeDrawer} />
        </Drawer>
      )}

      {/*/!* Preview Modal, desktop only *!/*/}
      {course && (coursePreview || coursePreviewStreamId) && (
        <ModalVideoPreview
          showVideo={showVideo}
          onClose={handleClose}
          title={course?.clean_title}
          videoUrl={coursePreview}
          captionsUrl={coursePreviewCaptions}
          muxStreamId={coursePreviewStreamId}
          courseId={courseId}
        />
      )}
    </Loading>
  )
}
